import React, { ReactElement } from 'react';
import { Trans, useTranslation } from 'next-i18next';

import Button from '@Hotels-at-Home/hah-fe-core/dist/components/Button';
import { sanitizeHtml } from '@Hotels-at-Home/hah-fe-core/dist/utils';

const WorksSection = (): ReactElement => {
  const { t } = useTranslation('', { keyPrefix: '$pages.$home.$worksSection' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const { t: tCommon } = useTranslation('', { keyPrefix: '$common' });
  const { t: tLink } = useTranslation('', { keyPrefix: '$local.$homePageLinks.$thisWorks' });

  return (
    <section className="base-section works-section">
      <div className="desktop-viewport">
        <div className="works">
          <h2 className="section-title" data-gsap="fade-up" data-gsap-delay="0.6">
            {t('thisTitle1')}&nbsp;
            <span className="accent-bar">
              {t('thisTitle2')}
              <em data-gsap="grow" data-gsap-delay="0.8" data-gsap-duration="0.8" />
            </span>
            &nbsp;{t('thisTitle3')}
          </h2>

          <div className="works-products works-products-one">
            <div className="works-product-wrap">
              <div data-gsap="fade" data-gsap-delay="0.03" className="stack">
                <p data-gsap="fade-up" data-gsap-delay="0.6" className="txt">
                  {t('carefullyCurated')}
                </p>
                <p data-gsap="fade-up" data-gsap-delay="0.65" className="works-product-wrap-button">
                  <Button
                    href={tLink('bathBody')}
                    id="btn_HP_this-works_shopnow"
                    variant="plain"
                    ariaLabel={`${tButton('shopNow')}: ${t('thisWorksCollection')}`}
                  >
                    {tButton('shopNow')}
                  </Button>
                </p>
              </div>
            </div>

            <div className="works-product-wrap">
              <a href={tLink('bathBody')}>
                <div className="branding-hero">
                  <p className="brand-copy" data-gsap="fade-up" data-gsap-delay="0.5">
                    <Trans i18nKey="nourishmentForBody" t={t} components={{ span: <span /> }} />
                  </p>
                  <div className="img-wrap-tall">
                    <picture>
                      <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                      <source srcSet="/images/homePage/worksSection/bath-background.webp" />
                      <img
                        data-gsap="zoom-out"
                        data-gsap-delay="0.3"
                        src="/images/homePage/worksSection/bath-background.webp"
                        alt={t('marriottThisWorks')}
                        loading="lazy"
                        width="1282"
                        height="801"
                        decoding="async"
                        fetchPriority="low"
                      />
                    </picture>
                    <div className="cinema-gif">
                      <picture>
                        <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                        <img
                          data-gsap="fade"
                          data-gsap-delay="0.35"
                          src="/images/homePage/worksSection/bath-overlay.gif"
                          alt={t('marriottThisWorks')}
                          loading="lazy"
                          width="1282"
                          height="801"
                          decoding="async"
                          fetchPriority="low"
                        />
                      </picture>
                    </div>
                  </div>
                </div>
              </a>
            </div>

            <div className="works-product-wrap">
              <a
                href={tLink('deepSleepCandle')}
                id="HP_this-works_candle"
                className="hover-btn-animation"
              >
                <picture>
                  <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                  <source srcSet="/images/homePage/worksSection/hp-this-works-candle.webp" />
                  <img
                    data-gsap="fade"
                    data-gsap-delay="0.3"
                    src="/images/homePage/worksSection/hp-this-works-candle.webp"
                    alt={tCommon('productName', { productName: t('thisWorksCandle') })}
                    loading="lazy"
                    width="828"
                    height="647"
                    decoding="async"
                    fetchPriority="low"
                  />
                </picture>
                <div className="hover-content">
                  <Button noTabbing ariaLabel={`${tButton('shopNow')}: ${t('thisWorksCandle')}`}>
                    {tButton('shopNow')}
                  </Button>
                </div>
              </a>
            </div>

            <div className="works-product-wrap">
              <a
                href={tLink('diffuserSet')}
                id="HP_this-works_diffuser-set"
                className="hover-btn-animation"
              >
                <picture>
                  <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                  <source srcSet="/images/homePage/worksSection/hp-this-works-diffuser.webp" />
                  <img
                    data-gsap="fade"
                    data-gsap-delay="0.3"
                    src="/images/homePage/worksSection/hp-this-works-diffuser.webp"
                    alt={tCommon('productName', { productName: t('thisWorksDiffuser') })}
                    loading="lazy"
                    width="828"
                    height="647"
                    decoding="async"
                    fetchPriority="low"
                  />
                </picture>
                <div className="hover-content">
                  <Button noTabbing ariaLabel={`${tButton('shopNow')}: ${t('thisWorksDiffuser')}`}>
                    {tButton('shopNow')}
                  </Button>
                </div>
              </a>
            </div>

            <div className="works-product-wrap">
              <div data-gsap="fade" data-gsap-delay="0.03" className="stack">
                <h2 data-gsap="fade-up" data-gsap-delay="0.6" className="thumb-title">
                  <Trans i18nKey="whyThisWorks" t={t} components={{ span: <span /> }} />
                </h2>
                <p data-gsap="fade-up" data-gsap-delay="0.75" className="txt">
                  {t('ourPartnership')}
                </p>
              </div>
            </div>

            <div className="works-product-wrap">
              <a
                href={tLink('spray')}
                id="HP_this-works_pillow-spray"
                className="hover-btn-animation"
              >
                <picture>
                  <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                  <source srcSet="/images/homePage/worksSection/hp-this-works-pillow-spray.webp" />
                  <img
                    data-gsap="fade"
                    data-gsap-delay="0.4"
                    src="/images/homePage/worksSection/hp-this-works-pillow-spray.webp"
                    alt={tCommon('productName', { productName: t('thisWorksPillowSpray') })}
                    loading="lazy"
                    width="828"
                    height="647"
                    decoding="async"
                    fetchPriority="low"
                  />
                </picture>
                <div className="hover-content">
                  <Button
                    noTabbing
                    ariaLabel={`${tButton('shopNow')}: ${t('thisWorksPillowSpray')}`}
                  >
                    {tButton('shopNow')}
                  </Button>
                </div>
              </a>
            </div>

            <div className="works-product-wrap">
              <div className="stack">
                <h2 data-gsap="fade-up" data-gsap-delay="0.6" className="thumb-title">
                  {t('sociallyResponsible')}
                </h2>
                <p data-gsap="fade-up" data-gsap-delay="0.75" className="txt">
                  {t('thisWorksProducts')}
                </p>
              </div>
            </div>

            <div className="works-product-wrap">
              <a
                href={tLink('bathBody')}
                id="HP_this-works_compilation"
                className="hover-btn-animation"
              >
                <picture>
                  <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                  <source srcSet="/images/homePage/worksSection/hp-this-works-compilation.webp" />
                  <img
                    data-gsap="fade"
                    data-gsap-delay="0.4"
                    src="/images/homePage/worksSection/hp-this-works-compilation.webp"
                    alt={tCommon('productName', { productName: t('thisWorksBathBody') })}
                    loading="lazy"
                    width="1697"
                    height="1343"
                    decoding="async"
                    fetchPriority="low"
                  />
                </picture>
                <div className="hover-content">
                  <Button noTabbing ariaLabel={`${tButton('shopNow')}: ${t('thisWorksBathBody')}`}>
                    {tButton('shopNow')}
                  </Button>
                </div>
              </a>
            </div>
          </div>
          <blockquote
            data-gsap="fade-up"
            data-gsap-target="children"
            data-gsap-delay="0.6"
            data-gsap-stagger="0.05"
          >
            <p className="quote">{t('eachThisWorks')}</p>
            <hr />
            <p className="quoter">{t('thisWorksCeo')}</p>
          </blockquote>
        </div>
      </div>
      <div className="hp-mobile-viewport">
        <div className="mob-works">
          <h2 className="mob-title">
            <span className="anchor-bar">
              <em data-gsap="grow" data-gsap-delay="0.06" data-gsap-duration="0.8" />
            </span>
            {t('thisTitle1')}&nbsp;
            {t('thisTitle2')}
            &nbsp;{t('thisTitle3')}
          </h2>
          <a className="mob-main-section" href={tLink('bathBody')} id="HP_thisWorks_mob">
            <picture>
              <source srcSet="/images/homePage/fill.gif" media="(min-width: 801px)" />
              <source srcSet="/images/homePage/worksSection/hp-this-works-mob.webp" />
              <img
                className="img"
                src="/images/homePage/worksSection/hp-this-works-mob.webp"
                alt={t('marriottThisWorks')}
                loading="lazy"
                width="786"
                height="419"
                decoding="async"
                fetchPriority="low"
              />
            </picture>
          </a>
          <div className="shop-list">
            <a
              className="shop-list-item"
              href={tLink('hairBodyCareSet')}
              id="CTA-Hair-Body-Set-mob"
            >
              <picture>
                <source srcSet="/images/homePage/fill.gif" media="(min-width: 801px)" />
                <source srcSet="/images/homePage/worksSection/marriott-hair-body-care-set-MAR-308-02-04-9_lrg.webp" />
                <img
                  className="img lazy"
                  src="/images/homePage/worksSection/marriott-hair-body-care-set-MAR-308-02-04-9_lrg.webp"
                  alt={tCommon('productName', { productName: t('hairBodyCare') })}
                  loading="lazy"
                  width="1123"
                  height="732"
                  decoding="async"
                  fetchPriority="low"
                />
              </picture>
              <h3
                className="mob-section mob-section-half-width txt"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(t('hairBodyCare')) }}
              />
            </a>
            <a className="shop-list-item" href={tLink('bodyLotion')} id="CTA-lotion-mob">
              <picture>
                <source srcSet="/images/homePage/fill.gif" media="(min-width: 801px)" />
                <source srcSet="/images/homePage/worksSection/marriott-in-the-zone-body-lotion-mar-302-02-03-10_lrg.webp" />
                <img
                  className="img lazy"
                  src="/images/homePage/worksSection/marriott-in-the-zone-body-lotion-mar-302-02-03-10_lrg.webp"
                  alt={tCommon('productName', { productName: t('bath') })}
                  loading="lazy"
                  width="1123"
                  height="732"
                  decoding="async"
                  fetchPriority="low"
                />
              </picture>
              <h3
                className="mob-section mob-section-half-width txt"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(t('bodyLotion')) }}
              />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorksSection;
